/**
 * ToastContainer - Displays toast notifications
 * 
 * Place this component once at the root of your app (e.g., in App.jsx)
 * 
 * Usage:
 * import { ToastContainer } from './components/ui/ToastContainer';
 * 
 * function App() {
 *   return (
 *     <>
 *       <Router>...</Router>
 *       <ToastContainer />
 *     </>
 *   );
 * }
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  subscribeToNotifications, 
  dismissNotification,
  confirmDialogState,
  resolveConfirm,
  NOTIFICATION_TYPES 
} from '../../services/notificationService';

// ============================================================================
// Toast Icons
// ============================================================================

const icons = {
  success: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  error: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  warning: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
};

// ============================================================================
// Toast Styles
// ============================================================================

const toastStyles = {
  success: {
    bg: 'bg-green-50 dark:bg-green-900/30',
    border: 'border-green-200 dark:border-green-800',
    icon: 'text-green-500 dark:text-green-400',
    text: 'text-green-800 dark:text-green-200',
    button: 'text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200'
  },
  error: {
    bg: 'bg-red-50 dark:bg-red-900/30',
    border: 'border-red-200 dark:border-red-800',
    icon: 'text-red-500 dark:text-red-400',
    text: 'text-red-800 dark:text-red-200',
    button: 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200'
  },
  warning: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/30',
    border: 'border-yellow-200 dark:border-yellow-800',
    icon: 'text-yellow-500 dark:text-yellow-400',
    text: 'text-yellow-800 dark:text-yellow-200',
    button: 'text-yellow-600 hover:text-yellow-800 dark:text-yellow-400 dark:hover:text-yellow-200'
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-900/30',
    border: 'border-blue-200 dark:border-blue-800',
    icon: 'text-blue-500 dark:text-blue-400',
    text: 'text-blue-800 dark:text-blue-200',
    button: 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200'
  }
};

// ============================================================================
// Single Toast Component
// ============================================================================

function Toast({ notification, onDismiss }) {
  const { id, message, type, title, dismissible, action, actionLabel } = notification;
  const style = toastStyles[type] || toastStyles.info;
  const icon = icons[type] || icons.info;

  const [isExiting, setIsExiting] = useState(false);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => onDismiss(id), 150); // Wait for exit animation
  };

  return (
    <div
      className={`
        flex items-start gap-3 p-4 rounded-lg border shadow-lg
        transform transition-all duration-150 ease-out
        ${isExiting ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}
        ${style.bg} ${style.border}
      `}
      role="alert"
    >
      {/* Icon */}
      <div className={`flex-shrink-0 ${style.icon}`}>
        {icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {title && (
          <p className={`font-medium ${style.text}`}>
            {title}
          </p>
        )}
        <p className={`text-sm ${style.text} ${title ? 'mt-1' : ''}`}>
          {message}
        </p>
        
        {/* Action Button */}
        {action && actionLabel && (
          <button
            onClick={() => {
              action();
              handleDismiss();
            }}
            className={`mt-2 text-sm font-medium underline ${style.button}`}
          >
            {actionLabel}
          </button>
        )}
      </div>

      {/* Dismiss Button */}
      {dismissible && (
        <button
          onClick={handleDismiss}
          className={`flex-shrink-0 ${style.button} p-1 rounded hover:bg-black/5 dark:hover:bg-white/5`}
          aria-label="Schließen"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Confirm Dialog Component
// ============================================================================

function ConfirmDialog() {
  const [state, setState] = useState(null);

  useEffect(() => {
    return confirmDialogState.subscribe(setState);
  }, []);

  if (!state?.isOpen) return null;

  const { message, title, confirmLabel, cancelLabel, variant } = state;

  const variantStyles = {
    danger: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    warning: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
    default: 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500'
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 dark:bg-black/70">
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {title}
        </h3>
        
        {/* Message */}
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          {message}
        </p>
        
        {/* Buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={() => resolveConfirm(false)}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            onClick={() => resolveConfirm(true)}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${variantStyles[variant] || variantStyles.default}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Toast Container (Main Export)
// ============================================================================

/**
 * ToastContainer - Renders all active notifications
 * 
 * Position: Top-right by default
 * Max notifications: 5 (oldest get pushed out)
 */
export function ToastContainer({ position = 'top-right', maxToasts = 5 }) {
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    return subscribeToNotifications(setNotifications);
  }, []);

  // Position classes
  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2'
  };

  // Only show the most recent maxToasts
  const visibleNotifications = notifications.slice(-maxToasts);

  // Use portal to render at document root level
  const toastContent = (
    <>
      {/* Toast Stack */}
      <div 
        className={`fixed z-50 flex flex-col gap-2 w-full max-w-sm ${positionClasses[position] || positionClasses['top-right']}`}
        aria-live="polite"
        aria-atomic="true"
      >
        {visibleNotifications.map((notification) => (
          <Toast
            key={notification.id}
            notification={notification}
            onDismiss={dismissNotification}
          />
        ))}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog />
    </>
  );

  // Render via portal to ensure it's always on top
  if (typeof document !== 'undefined') {
    return createPortal(toastContent, document.body);
  }

  return toastContent;
}

// ============================================================================
// Export
// ============================================================================

export default ToastContainer;
